import Notification, {
  NotificationProps,
} from '@webclient/components/notification/components/notification';
import NotificationContainer from '@webclient/components/notification/components/notification-container';
import { shouldHideNotification } from '@webclient/components/notification/utils';

import { toast, ToastContainerProps } from 'react-toastify';

type ShowNotificationOptions = Omit<NotificationProps, 'closeToast' | 'id'> &
  Partial<ToastContainerProps> & {
    id?: string;
    hideProgressBar?: boolean;
  };

function showNotification({
  id,
  autoClose = 3,
  hideProgressBar = false,
  position = 'top-right',
  ...props
}: ShowNotificationOptions) {
  const toastId: string = id ?? Math.random().toString(36).slice(2);

  if (id && shouldHideNotification(id)) {
    return toastId || '';
  }

  if (!toast.isActive(toastId)) {
    toast(
      <Notification
        {...props}
        id={toastId}
      />,
      {
        type: props.type,
        toastId: toastId,
        position: position,
        closeButton: false,
        autoClose: typeof autoClose === 'number' ? autoClose * 1000 : false,
        hideProgressBar: hideProgressBar,
        ...props,
      },
    );
  }

  return toastId;
}

type NotificationAPI = {
  (props: ShowNotificationOptions): string;
  success: (
    options?: Omit<ShowNotificationOptions, 'type' | 'title'>,
  ) => string;
  error: (options?: Omit<ShowNotificationOptions, 'type' | 'title'>) => string;
  info: (options?: Omit<ShowNotificationOptions, 'type' | 'title'>) => string;
  warning: (
    options?: Omit<ShowNotificationOptions, 'type' | 'title'>,
  ) => string;
};

const notification: NotificationAPI = Object.assign(
  (props: ShowNotificationOptions) => showNotification(props),
  {
    success: (props: ShowNotificationOptions) =>
      showNotification({ type: 'success', ...props }),
    error: (props: ShowNotificationOptions) =>
      showNotification({ type: 'error', ...props }),
    info: (props: ShowNotificationOptions) =>
      showNotification({ type: 'info', ...props }),
    warning: (props: ShowNotificationOptions) =>
      showNotification({ type: 'warning', ...props }),
  },
) as NotificationAPI;

export { notification, NotificationContainer };
